/**
 * Plugin Loader — auto-discovers and registers user plugin files
 *
 * Discovery strategies (in priority order):
 *   1. Config-based: `plugins` array in agent-runtime.config.yaml
 *   2. Convention-based: scan `src/plugins/` for files exporting AgentPlugin
 *
 * Each discovered module is dynamically import()'d. Exports that satisfy the
 * AgentPlugin interface (have an `id` string property) are registered with
 * the pluginRegistry singleton.
 */

import { existsSync } from "node:fs";
import { readdir } from "node:fs/promises";
import { resolve, join } from "node:path";
import { pluginRegistry, type AgentPlugin } from "./plugin.js";

const PLUGIN_EXTENSIONS = [".ts", ".js", ".mts", ".mjs"];

function isPluginLike(obj: unknown): obj is AgentPlugin {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof (obj as Record<string, unknown>).id === "string"
  );
}

/**
 * Scan a directory for files that may export plugins.
 * Returns absolute paths of candidate files.
 */
async function scanDirectory(dir: string): Promise<string[]> {
  if (!existsSync(dir)) return [];

  const entries = await readdir(dir, { withFileTypes: true });
  return entries
    .filter(
      (e) =>
        e.isFile() &&
        PLUGIN_EXTENSIONS.some((ext) => e.name.endsWith(ext)) &&
        !e.name.startsWith("_") &&
        !e.name.endsWith(".d.ts") &&
        !e.name.endsWith(".test.ts") &&
        !e.name.endsWith(".spec.ts"),
    )
    .map((e) => resolve(dir, e.name));
}

/**
 * Import a single module and register any AgentPlugin exports.
 * Returns the number of plugins registered from this module.
 */
async function loadPluginModule(modulePath: string): Promise<number> {
  const mod = await import(new URL(`file://${modulePath}`).href);
  let count = 0;

  for (const exportValue of Object.values(mod)) {
    if (isPluginLike(exportValue)) {
      try {
        pluginRegistry.register(exportValue);
        count++;
      } catch {
        // Already registered — skip duplicate
      }
    }
  }

  return count;
}

export interface PluginLoadResult {
  loaded: number;
  files: string[];
  errors: Array<{ file: string; error: string }>;
}

/**
 * Load plugins from explicit paths (config-based discovery).
 */
export async function loadPluginsFromPaths(
  paths: string[],
  baseDir: string,
): Promise<PluginLoadResult> {
  const result: PluginLoadResult = { loaded: 0, files: [], errors: [] };

  for (const p of paths) {
    const absPath = resolve(baseDir, p);
    if (!existsSync(absPath)) {
      result.errors.push({ file: p, error: `File not found: ${absPath}` });
      continue;
    }

    try {
      const count = await loadPluginModule(absPath);
      result.loaded += count;
      result.files.push(absPath);
    } catch (err) {
      result.errors.push({
        file: p,
        error: (err as Error).message,
      });
    }
  }

  return result;
}

/**
 * Load plugins using convention-based directory scanning.
 * Scans `<projectDir>/src/plugins/` by default.
 */
export async function loadPluginsFromDirectory(
  dir: string,
): Promise<PluginLoadResult> {
  const result: PluginLoadResult = { loaded: 0, files: [], errors: [] };
  const candidates = await scanDirectory(dir);

  for (const filePath of candidates) {
    try {
      const count = await loadPluginModule(filePath);
      result.loaded += count;
      result.files.push(filePath);
    } catch (err) {
      result.errors.push({
        file: filePath,
        error: (err as Error).message,
      });
    }
  }

  return result;
}

/**
 * Auto-discover and load plugins. Uses config-based paths if provided,
 * otherwise falls back to convention-based scanning of src/plugins/.
 */
export async function loadPlugins(options: {
  pluginPaths?: string[];
  projectDir: string;
}): Promise<PluginLoadResult> {
  if (options.pluginPaths && options.pluginPaths.length > 0) {
    return loadPluginsFromPaths(options.pluginPaths, options.projectDir);
  }

  const conventionDir = join(options.projectDir, "src", "plugins");
  return loadPluginsFromDirectory(conventionDir);
}
